import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowUp, Flag, ImagePlus, Mail, X } from "lucide-react";
import { uploadPhoto } from "@/components/photoUpload";
import { Lightbox } from "@/components/Posts";
import { useDialog } from "@/components/Dialogs";
import { errText, when } from "@/components/ContentBits";

const LIMIT = 1000;

function hhmm(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function Chat({ username, onBack, onChanged }) {
  const dialog = useDialog();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reported, setReported] = useState({});
  const end = useRef(null);
  const [photo, setPhoto] = useState(null);        // the photo waiting to be sent: {id, url, thumb}
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);    // a photo opened full size

  const load = useCallback(() => {
    api.get(`/messages/with/${encodeURIComponent(username)}`).then((r) => { setMsgs(r.data.messages); onChanged(); })
      .catch((e) => { setMsgs((m) => m || []); if (e?.response?.status === 404) toast.error("You can only message your friends."); });
  }, [username, onChanged]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const f = () => load();
    window.addEventListener("focus", f);
    return () => { clearInterval(t); window.removeEventListener("focus", f); };
  }, [load]);
  useEffect(() => { if (msgs && end.current && end.current.scrollIntoView) end.current.scrollIntoView({ block: "end" }); }, [msgs?.length]);

  const pickPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const v = await uploadPhoto(file, "dm");
      if (photo) api.delete(`/media/${photo.id}`).catch(() => {});
      setPhoto(v);
    } catch (err) { toast.error(errText(err, "Could not add that photo. Please try another.")); }
    finally { setUploading(false); }
  };
  const dropPhoto = () => { if (photo) api.delete(`/media/${photo.id}`).catch(() => {}); setPhoto(null); };

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !photo) || busy || uploading) return;
    setBusy(true);
    try { const r = await api.post(`/messages/with/${encodeURIComponent(username)}`, { text, media_id: photo ? photo.id : null }); setMsgs((m) => ((m || []).some((x) => x.id === r.data.id) ? m : [...(m || []), r.data])); setText(""); setPhoto(null); }
    catch (err) { toast.error(errText(err, "Could not send that. Please try again.")); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!(await dialog.confirm({ title: "Delete this message?", body: "It will disappear for both of you.", confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/messages/${m.id}`); setMsgs((a) => a.filter((x) => x.id !== m.id)); }
    catch { toast.error("Could not delete that. Please try again."); }
  };
  const report = async (m) => {
    const reason = await dialog.prompt({ title: "Report this message", body: "We will see this message and the few before it. The person won't be told it was you.",
      label: "What is wrong with it? (optional)", maxLength: 300, confirmLabel: "Send report" });
    if (reason === null) return;
    try { await api.post(`/messages/${m.id}/report`, { reason }); setReported((r) => ({ ...r, [m.id]: true })); }
    catch { toast.error("Could not send that report. Please try again."); }
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col rounded-2xl border border-border bg-card" data-testid="chat">
      {dialog.node}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <button type="button" onClick={onBack} data-testid="chat-back" className="rounded-full border border-border px-3 py-1 text-sm hover:border-[#C89B3C]/60">← Messages</button>
        <Link to={`/u/${username}`} className="font-serif text-lg font-semibold hover:underline" data-testid="chat-name">{username}</Link>
      </div>
      <div className="max-h-[55vh] min-h-[16rem] space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
        {!msgs ? <p className="text-muted-foreground">Loading…</p> : msgs.length === 0 ? (
          <p className="py-10 text-center text-muted-foreground" data-testid="chat-empty">No messages yet. Say hello to {username}.</p>
        ) : msgs.map((m) => (
          <div key={m.id} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`} data-testid={m.mine ? "msg-mine" : "msg-theirs"}>
            {m.has_image && (m.image
              ? <button type="button" onClick={() => setViewing(m.image)} data-testid="msg-image" aria-label="Open photo" className="mb-1 block max-w-[75%] overflow-hidden rounded-2xl border border-border"><img src={m.image.thumb || m.image.url} alt="Photo in this message" loading="lazy" className="max-h-64 w-auto object-cover" /></button>
              : <div className="mb-1 rounded-2xl border border-dashed border-border px-4 py-2 text-sm text-muted-foreground" data-testid="msg-image-missing">Photo unavailable</div>)}
            {m.text && <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-base leading-relaxed ${m.mine ? "bg-[#C89B3C] text-black" : "bg-secondary text-foreground"}`} data-testid="msg-text">{m.text}</div>}
            <p className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{hhmm(m.created_at)}{m.mine && m.read ? " · Seen" : ""}</span>
              {m.mine
                ? <button type="button" onClick={() => remove(m)} data-testid="msg-delete" className="underline hover:text-red-400">Delete</button>
                : reported[m.id] ? <span data-testid="msg-reported">Thanks, we'll take a look.</span>
                : <button type="button" onClick={() => report(m)} data-testid="msg-report" className="inline-flex items-center gap-1 underline hover:text-foreground"><Flag className="h-3 w-3" /> Report</button>}
            </p>
          </div>
        ))}
        <div ref={end} />
      </div>
      {viewing && <Lightbox images={[viewing]} index={0} onIndex={() => {}} onClose={() => setViewing(null)} />}
      <form onSubmit={send} className="border-t border-border p-3">
        {(photo || uploading) && (
          <div className="mb-2 flex items-center gap-3" data-testid="msg-photo-preview">
            {photo ? <img src={photo.thumb || photo.url} alt="Photo ready to send" className="h-16 w-16 rounded-lg border border-border object-cover" /> : <span className="text-sm text-muted-foreground">Adding photo…</span>}
            {photo && <button type="button" onClick={dropPhoto} aria-label="Remove photo" data-testid="msg-photo-remove" className="rounded-full border border-border p-1.5 hover:border-red-400"><X className="h-4 w-4" /></button>}
          </div>
        )}
        <label htmlFor="msg-box" className="sr-only">Message {username}</label>
        <textarea id="msg-box" data-testid="msg-box" rows={2} maxLength={LIMIT} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Message ${username}…`}
          className="w-full rounded-xl border border-[#C89B3C]/40 bg-background px-4 py-3 text-base leading-relaxed focus:border-[#C89B3C] focus:outline-none" />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-3 py-2 text-sm hover:bg-[#C89B3C]/15">
              <ImagePlus className="h-4 w-4" aria-hidden="true" /> Photo
              <input type="file" accept="image/*" className="sr-only" data-testid="msg-photo-input" onChange={pickPhoto} />
            </label>
            <span className="text-xs text-muted-foreground">Only you and {username} can see this. {text.length} / {LIMIT}</span>
          </span>
          <button type="submit" disabled={busy || uploading || (!text.trim() && !photo)} data-testid="msg-send" className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-6 py-2.5 text-base font-semibold text-black disabled:opacity-50"><ArrowUp className="h-4 w-4" /> {busy ? "Sending…" : "Send"}</button>
        </div>
      </form>
    </div>
  );
}

/** Private messages between friends. `open` is a username to open straight away; onUnread reports the unread total. */
export default function MessagesTab({ open, onOpened, onUnread }) {
  const [box, setBox] = useState(null);
  const [friends, setFriends] = useState([]);
  const [active, setActive] = useState(open || null);
  const [emailOn, setEmailOn] = useState(true);

  useEffect(() => { if (open) { setActive(open); onOpened && onOpened(); } }, [open, onOpened]);
  const load = useCallback(() => {
    api.get("/messages").then((r) => { setBox(r.data); onUnread && onUnread(r.data.unread || 0); }).catch(() => setBox((b) => b || { conversations: [], unread: 0 }));
  }, [onUnread]);
  useEffect(() => {
    load();
    api.get("/friends").then((r) => setFriends(r.data.friends || [])).catch(() => setFriends([]));
    api.get("/messages/settings").then((r) => setEmailOn(!!r.data.email)).catch(() => {});
    const t = setInterval(load, 30000);
    const f = () => load();
    window.addEventListener("focus", f);
    return () => { clearInterval(t); window.removeEventListener("focus", f); };
  }, [load]);

  const toggleEmail = async (on) => {
    setEmailOn(on);
    try { await api.put("/messages/settings", { email: on }); }
    catch { setEmailOn(!on); toast.error("Could not save that. Please try again."); }
  };

  if (active) return <Chat username={active} onBack={() => { setActive(null); load(); }} onChanged={load} />;

  const talked = new Set((box?.conversations || []).map((c) => c.username));
  const others = friends.filter((f) => !talked.has(f.username));
  return (
    <div className="mx-auto max-w-2xl space-y-6 md:mx-0" data-testid="messages-tab">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Messages</h2>
        <p className="text-sm text-muted-foreground">Private messages with your friends. Only the two of you can read them.</p>
      </div>
      {!box ? <p className="text-muted-foreground">Loading…</p> : box.conversations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="messages-empty">
          <Mail className="mx-auto h-8 w-8 text-[#C89B3C]" />
          <p className="mt-3 font-serif text-xl font-semibold">No messages yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{friends.length ? "Pick a friend below to start a conversation." : "Add some friends first, then you can message them here."}</p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card" data-testid="conversations">
          {box.conversations.map((c) => (
            <li key={c.username}>
              <button type="button" onClick={() => setActive(c.username)} data-testid={`conv-${c.username}`} className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-secondary/60">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C] font-serif text-lg font-bold text-black" aria-hidden="true">{c.username[0].toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-base ${c.unread ? "font-bold" : "font-medium"}`}>{c.username}</span>
                  <span className={`block truncate text-sm ${c.unread ? "text-foreground" : "text-muted-foreground"}`}>{c.last_from_me ? "You: " : ""}{c.preview}</span>
                </span>
                <span className="flex flex-shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground">
                  <span>{when(c.last_at)}</span>
                  {c.unread > 0 && <span className="rounded-full bg-[#C89B3C] px-2 py-0.5 font-bold text-black" data-testid="conv-unread">{c.unread}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <section data-testid="start-chat">
          <p className="text-sm font-medium">Start a conversation</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {others.map((f) => (
              <button key={f.id} type="button" onClick={() => setActive(f.username)} data-testid={`start-${f.username}`} className="rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15">{f.username}</button>
            ))}
          </div>
        </section>
      )}
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input type="checkbox" data-testid="msg-email-toggle" checked={emailOn} onChange={(e) => toggleEmail(e.target.checked)} /> Email me when a friend sends me a message (the email never contains the message)
      </label>
    </div>
  );
}